import React from 'react';
import { RequirementsProps } from '../../tools/context';
import { SanityColor } from '../../tools/sanity';
import { Picto, pictos } from '../Picto';
import { getPictoToneClass, PictoColorMode, resolvePictoFill } from './pictoColor';
import { mergeResponsiveSpacingVars } from './spacing';

export interface PageButtonProps {
  requirements: RequirementsProps;
  text: string;
  link: string;
  linkTarget?: '_self' | '_blank';
  align?: 'left' | 'center' | 'right';
  leftIcon?: keyof typeof pictos;
  rightIcon?: keyof typeof pictos;
  pictoColorMode?: PictoColorMode;
  pictoColor?: SanityColor;
  fontFamily?: 'main' | 'title';
  textColor?: SanityColor;
  backgroundColor?: SanityColor;
  hasBorder?: boolean;
  borderWidth?: number;
  borderColor?: SanityColor;
  margin?: string;
  marginMobile?: string;
  padding?: string;
  paddingMobile?: string;
}

const FONT_FAMILY: Record<'main' | 'title', string> = {
  main: 'var(--mainFont)',
  title: 'var(--titleFont)',
};

const Renderer = ({
  text,
  link,
  linkTarget = '_self',
  align = 'center',
  leftIcon,
  rightIcon,
  pictoColorMode = 'text',
  pictoColor,
  fontFamily = 'main',
  textColor,
  backgroundColor,
  hasBorder,
  borderWidth,
  borderColor,
  margin,
  marginMobile,
  padding,
  paddingMobile,
}: PageButtonProps) => {
  if (!text || !link) return null;

  const pictoFill = resolvePictoFill(pictoColorMode, textColor, pictoColor);
  const pictoToneClass = getPictoToneClass(pictoColorMode);

  const renderPicto = (iconKey: keyof typeof pictos) => (
    <span className={['PageButton__picto', pictoToneClass].filter(Boolean).join(' ')}>
      <Picto iconKey={iconKey} fill={pictoFill} />
    </span>
  );

  const opensInNewTab = linkTarget === '_blank';
  const wrapperStyle: React.CSSProperties = mergeResponsiveSpacingVars('page-button', {
    margin,
    marginMobile,
  });
  const linkStyle: React.CSSProperties = {
    ...mergeResponsiveSpacingVars('page-button', { padding, paddingMobile }),
    color: textColor?.hex,
    backgroundColor: backgroundColor?.hex,
    fontFamily: FONT_FAMILY[fontFamily] ?? FONT_FAMILY.main,
    ...(hasBorder && borderWidth != null
      ? {
          borderWidth: `${borderWidth}px`,
          borderStyle: 'solid',
          borderColor: borderColor?.hex ?? 'currentColor',
        }
      : { border: 'none' }),
  };

  return (
    <div className={`PageButton PageButton--align-${align}`} style={wrapperStyle}>
      <a
        href={link}
        target={linkTarget}
        rel={opensInNewTab ? 'noopener noreferrer' : undefined}
        className="PageButton__link"
        style={linkStyle}
      >
        {leftIcon ? renderPicto(leftIcon) : null}
        <span className="PageButton__text">{text}</span>
        {rightIcon ? renderPicto(rightIcon) : null}
        {opensInNewTab ? (
          <span className="visually-hidden"> (ouvre un nouvel onglet)</span>
        ) : null}
      </a>
    </div>
  );
};

const preloader = async (data: PageButtonProps) => data;

const PageButton = { Renderer, preloader };

export default PageButton;
export { Renderer, preloader };
